import React, { useEffect, useState, useRef, useContext } from 'react';
import axiosInstance from '../../api/axiosInstance';
import { AuthContext } from '../../context/AuthContext';
import './FileServer.css';
import {
  FaFolder, FaFileAlt, FaArrowLeft, FaSearch, FaTimes,
  FaDownload, FaHome, FaSyncAlt, FaTrashAlt, FaExclamationTriangle,
  FaFilePdf, FaFileWord, FaFileExcel, FaFilePowerpoint, FaFileImage, FaFileCode,
  FaCloudUploadAlt, FaEraser, FaRegTimesCircle
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CLOUDINARY_CLOUD_NAME = 'dahroudyn'; // Replace with your Cloudinary name

function FileServer() {
  const { currentGroup } = useContext(AuthContext);
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentPath, setCurrentPath] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [reloadFlag, setReloadFlag] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const normalizePath = (path) => (path || '').replace(/^\/|\/$/g, '');

  useEffect(() => {
    fetchData();
  }, [currentPath, searchQuery, reloadFlag]);

  const fetchData = async () => {
    try {
      const isSearching = searchQuery.trim() !== '';
      const res = await axiosInstance.get('/cloud/list', {
        params: {
          folder: isSearching ? '' : currentPath,
          recursive: isSearching,
        },
      });

      const allFiles = res.data.files || [];

      if (!isSearching) {
        setFiles(allFiles);
        setFolders(res.data.folders || []);
      } else {
        const matchedFiles = allFiles.filter(file =>
          file?.public_id?.toLowerCase().includes(searchQuery)
        );

        const folderSet = new Set();
        matchedFiles.forEach(file => {
          const parts = file.public_id.split('/');
          if (parts.length > 1) {
            folderSet.add(parts.slice(0, -1).join('/'));
          }
        });

        setFiles(matchedFiles);
        setFolders(Array.from(folderSet));
      }
    } catch (err) {
      console.error('❌ Fetch error:', err);
    }
  };

  const handleFolderClick = (folder) => setCurrentPath(normalizePath(folder));

  const goBack = () => {
    const parts = normalizePath(currentPath).split('/');
    parts.pop();
    setCurrentPath(parts.join('/'));
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
    setUploadProgress(0);
    setUploadComplete(false);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    // Use currentGroup ID or fallback to 'admin_upload' if not set
    const groupId = currentGroup?._id || 'admin_upload';

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('folder', currentPath);
    formData.append('groupId', groupId);

    setUploadProgress(0);
    setUploadComplete(false);

    try {
      await axiosInstance.post('/cloud/upload', formData, {
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        },
      });

      setUploadProgress(100);
      setUploadComplete(true);
      setSelectedFile(null);

      setTimeout(() => {
        setReloadFlag(prev => !prev);
      }, 1500);

      setTimeout(() => {
        setUploadProgress(0);
        setUploadComplete(false);
      }, 2000);
    } catch (err) {
      console.error('Upload error:', err);
    }
  };

  const getFileType = (file) => {
    const ext = (file.format || '').toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
    if (ext === 'pdf') return 'pdf';
    if (['doc', 'docx'].includes(ext)) return 'word';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'excel';
    if (['ppt', 'pptx'].includes(ext)) return 'ppt';
    if (['js', 'html', 'css', 'json', 'py', 'java'].includes(ext)) return 'code';
    return 'other';
  };

  const getSafeFilename = (file) => {
    const original = file?.public_id?.split('/').pop() || 'file';
    const hasExtension = new RegExp(`\\.${file.format}$`, 'i').test(original);
    const safeBase = original.replace(/[^\w\-\.]/g, '_');
    return hasExtension ? safeBase : `${safeBase}.${file.format}`;
  };

  const getCloudinaryUrl = (file) => {
    if (!file?.cloudinaryId && !file?.public_id) return '';
    const publicId = file.public_id || file.cloudinaryId;
    const format = (file.format || '').toLowerCase();
    const imageFormats = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
    const resourceType = imageFormats.includes(format) ? 'image' : 'raw';

    return `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/${resourceType}/upload/${publicId}`;
  };

  const downloadFile = async (file) => {
    try {
      const safeName = getSafeFilename(file);
      const format = (file.format || '').toLowerCase();
      const imageFormats = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
      const resourceType = imageFormats.includes(format) ? 'image' : 'raw';

      let downloadUrl = getCloudinaryUrl(file);

      if (resourceType !== 'image') {
        const parts = downloadUrl.split('/upload/');
        if (parts.length === 2) {
          downloadUrl = `${parts[0]}/upload/fl_attachment/${parts[1]}`;
        }
      }

      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = safeName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error('❌ Download failed:', err);
      alert('Download failed. See console for details.');
    }
  };

  const renderPreviewModal = () => {
    if (!previewFile) return null;
    const type = getFileType(previewFile);
    const url = getCloudinaryUrl(previewFile);

    return (
      <div className="modal-overlay" onClick={() => setPreviewFile(null)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <h2>{getSafeFilename(previewFile)}</h2>
          {type === 'image' && <img src={url} alt="Preview" className="modal-image" />}
          {type === 'pdf' && (
            <iframe src={url} title="PDF Preview" className="modal-pdf" frameBorder="0" width="100%" height="700px" />
          )}
          {type === 'other' && <div className="modal-other">No preview available</div>}
          <div className="modal-actions" style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button className="modal-download" onClick={() => downloadFile(previewFile)}>
              <FaDownload style={{ marginRight: '6px' }} /> Download
            </button>
            <button
              className="modal-delete"
              onClick={() => handleDeleteClick(previewFile)}
              style={{ backgroundColor: '#ff4d4f', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
              <FaTrashAlt style={{ marginRight: '6px' }} /> Delete
            </button>
            <button className="modal-close" onClick={() => setPreviewFile(null)} style={{ display: 'flex', alignItems: 'center' }}>
              <FaTimes style={{ marginRight: '6px' }} /> Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const confirmDelete = async () => {
    if (!fileToDelete) return;

    // Close Modals
    setShowConfirmModal(false);

    // Show deleting toast
    const toastId = toast.loading("Deleting file...");

    try {
      const publicId = fileToDelete.public_id || fileToDelete.cloudinaryId;
      await axiosInstance.delete(`/pinecone/file/undefined?public_id=${encodeURIComponent(publicId)}`);

      toast.update(toastId, { render: "✅ File deleted successfully.", type: "success", isLoading: false, autoClose: 3000 });
      setPreviewFile(null); // Close preview modal if open
      setFileToDelete(null);
      setReloadFlag(prev => !prev);
    } catch (err) {
      console.error('Delete error:', err);
      toast.update(toastId, { render: "❌ Failed to delete file.", type: "error", isLoading: false, autoClose: 3000 });
    }
  };

  const handleDeleteClick = (file) => {
    setFileToDelete(file);
    setShowConfirmModal(true);
  };

  const renderConfirmModal = () => {
    if (!showConfirmModal || !fileToDelete) return null;
    return (
      <div className="modal-overlay" onClick={() => setShowConfirmModal(false)} style={{ backdropFilter: 'blur(5px)' }}>
        <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px', textAlign: 'center', padding: '30px' }}>
          <FaExclamationTriangle size={50} color="#ff4d4f" style={{ marginBottom: '15px' }} />
          <h3 style={{ margin: '0 0 10px', color: '#333' }}>Permanently Delete?</h3>
          <p style={{ color: '#666', marginBottom: '25px', lineHeight: '1.5' }}>
            Are you sure you want to delete <strong>"{getSafeFilename(fileToDelete)}"</strong>?
            <br /><br />
            This action <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>cannot be undone</span> and will remove the file from your knowledge base and search results.
          </p>
          <div className="modal-actions" style={{ justifyContent: 'center', gap: '15px' }}>
            <button
              onClick={() => setShowConfirmModal(false)}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: '1px solid #ddd',
                background: 'white',
                cursor: 'pointer',
                fontWeight: '500',
                color: '#555'
              }}
            >
              Cancel
            </button>
            <button
              onClick={confirmDelete}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                background: '#ff4d4f',
                color: 'white',
                cursor: 'pointer',
                fontWeight: '600',
                boxShadow: '0 4px 12px rgba(255, 77, 79, 0.3)'
              }}
            >
              Delete File
            </button>
          </div>
        </div>
      </div>
    );
  };

  const filteredFolders = (folders || [])
    .filter(folder => typeof folder === 'string' && folder)
    .filter(folder => {
      const normalizedFolder = normalizePath(folder);
      const normalizedCurrent = normalizePath(currentPath);
      const isChild =
        (!normalizedCurrent && normalizedFolder.split('/').length === 1) ||
        (normalizedFolder.startsWith(normalizedCurrent + '/') &&
          normalizedFolder.split('/').length === normalizedCurrent.split('/').length + 1);

      const name = folder.split('/').pop().toLowerCase();
      return isChild && name.includes(searchQuery);
    });

  const filteredFiles = (files || []).filter(file => {
    if (!file || !file.public_id) return false;

    const normalizedPublicId = normalizePath(file.public_id);
    const normalizedCurrent = normalizePath(currentPath);

    const pathParts = normalizedPublicId.split('/');
    const currentParts = normalizedCurrent.split('/').filter(Boolean);

    const isDirectChild =
      (normalizedCurrent === '' && pathParts.length === 1) ||
      (normalizedPublicId.startsWith(normalizedCurrent + '/') &&
        pathParts.length === currentParts.length + 1);

    const name = pathParts[pathParts.length - 1].toLowerCase();
    return isDirectChild && name.includes(searchQuery);
  });

  return (
    <div style={{
      backgroundColor: '#1d1c2c',
      padding: '2rem',
      borderRadius: '20px',
      boxShadow: '0 4px 30px rgba(17, 23, 57, 0.06)',
      color: '#1e1e1e',
      maxWidth: '80vw',
      minHeight: '100vh',
    }}>
      <div className="app-container">
        <div className="title-card">
          <div className="icon-container">
            <FaFileAlt size={50} color="#4facfe" />
          </div>
          <h1 className="title-text">Document Management System (DMS)</h1>
        </div>

        <div className="navigation-header">
          <div className="breadcrumbs">
            <span className="breadcrumb-part" onClick={() => setCurrentPath('')}><FaHome /> Home</span>
            {currentPath &&
              normalizePath(currentPath).split('/').map((part, index, arr) => {
                const path = arr.slice(0, index + 1).join('/');
                return (
                  <span key={index}>
                    <span className="breadcrumb-separator">➜</span>
                    <span className="breadcrumb-part" onClick={() => setCurrentPath(path)}>
                      {part}
                    </span>
                  </span>
                );
              })}
          </div>

          <div className="subheader">📂 Browsing: <strong>{currentPath || 'Home'}</strong></div>

          {currentPath && (
            <button className="back-button" onClick={goBack}>
              <FaArrowLeft style={{ marginRight: '5px' }} /> Back
            </button>
          )}
        </div>

        <div className="upload-section">
          <input
            id="admin-file-upload"
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ color: 'white' }}
            accept="image/png, image/jpeg, image/gif, image/webp, application/pdf, .doc, .docx, .xls, .xlsx, .ppt, .pptx, .txt"
          />
          <button onClick={() => {
            if (!selectedFile) {
              return toast.error("Please select a file first!");
            }
            handleUpload();
          }}>
            <FaCloudUploadAlt size={18} style={{ marginRight: '6px' }} /> Upload
          </button>
          <button onClick={() => window.location.reload()}>
            <FaSyncAlt size={16} style={{ marginRight: '6px' }} /> Reload
          </button>
          <button onClick={() => {
            setSelectedFile(null);
            setUploadProgress(0);
            setUploadComplete(false);
            if (fileInputRef.current) fileInputRef.current.value = null;
          }}>
            <FaEraser size={16} style={{ marginRight: '6px' }} /> Clear
          </button>

          <div className="upload-feedback">
            {uploadProgress > 0 && (
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${uploadProgress}%` }}></div>
              </div>
            )}
            {uploadComplete && <div className="upload-success">✅ Upload complete!</div>}
          </div>
        </div>

        <div className="search-bar">
          <input
            type="text"
            placeholder="🔍 Search files or folders..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value.toLowerCase())}
          />
          {searchQuery && (
            <button className="clear-search" onClick={() => setSearchQuery('')}>
              <FaRegTimesCircle size={16} style={{ marginRight: '4px' }} /> Clear
            </button>
          )}
        </div>

        <div className="grid">
          {filteredFolders.map((folder, i) => (
            <div key={i} className="card folder" onClick={() => handleFolderClick(folder)}>
              <div className="icon"><FaFolder color="#f0c000" /></div>
              <div className="label">{folder.replace(normalizePath(currentPath) + '/', '')}</div>
              <div className="path-text">{folder}</div>
            </div>
          ))}

          {filteredFiles.map((file, i) => {
            const fileType = getFileType(file);
            const label = normalizePath(currentPath)
              ? file.public_id.replace(normalizePath(currentPath) + '/', '')
              : file.public_id;
            let icon;
            switch (fileType) {
              case 'image':
                icon = <img src={getCloudinaryUrl(file)} alt="thumb" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: '4px' }} />;
                break;
              case 'pdf':
                icon = <FaFilePdf size={32} color="#e11d48" />;
                break;
              case 'word':
                icon = <FaFileWord size={32} color="#2563eb" />;
                break;
              case 'excel':
                icon = <FaFileExcel size={32} color="#16a34a" />;
                break;
              case 'ppt':
                icon = <FaFilePowerpoint size={32} color="#c026d3" />;
                break;
              case 'code':
                icon = <FaFileCode size={32} color="#4b5563" />;
                break;
              default:
                icon = <FaFileAlt size={32} color="#9ca3af" />;
            }

            return (
              <div key={i} className="card file" onClick={() => setPreviewFile(file)}>
                <div className="icon">{icon}</div>
                <div className="label">{label}</div>
                <div className="path-text">{file.public_id}</div>
              </div>
            );
          })}
        </div>

        {renderPreviewModal()}
        {renderConfirmModal()}
      </div>
    </div>
  );
}

export default FileServer;
